import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, Paper, IconButton, Divider, Toolbar, InputAdornment, Tooltip, Slider, CssBaseline
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';

const DEFAULT_TEXTS = ["PANAROMIC VIEW", "41", "42", "43", "44", "45"];
const DEFAULT_COLOR = '#ffffff';
const DEFAULT_FONT_SIZE = 30;
const DEFAULT_FONT_FAMILY = 'Arial';

const PanoramicMarker = () => {
    // Canvas & State Refs
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);
    const [imageObj, setImageObj] = useState(null);
    const [originalFileName, setOriginalFileName] = useState('');

    // Sidebar text snippets state (synced with localStorage)
    const [sidebarTexts, setSidebarTexts] = useState([]);
    const [newText, setNewText] = useState('');

    // Canvas drawing states
    const [textsOnCanvas, setTextsOnCanvas] = useState([]);
    const [selectedTextId, setSelectedTextId] = useState(null);

    // Tools state
    const [color, setColor] = useState(() => localStorage.getItem('panoramicMarkerColor') || DEFAULT_COLOR);
    const [fontSize, setFontSize] = useState(() => {
        const stored = localStorage.getItem('panoramicMarkerFontSize');
        return stored ? parseInt(stored, 10) : DEFAULT_FONT_SIZE;
    });

    // Mouse tracking for drag
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Zoom state
    const [zoom, setZoom] = useState(100);

    // Sidebar list reorder refs
    const dragItem = useRef();
    const dragOverItem = useRef();

    // Load initial data from localStorage
    useEffect(() => {
        const storedTexts = localStorage.getItem('panoramicSidebarTexts');
        if (storedTexts) {
            setSidebarTexts(JSON.parse(storedTexts));
        } else {
            // Seed defaults
            setSidebarTexts(DEFAULT_TEXTS);
            localStorage.setItem('panoramicSidebarTexts', JSON.stringify(DEFAULT_TEXTS));
        }

        const storedCanvasData = localStorage.getItem('panoramicCanvasTexts');
        if (storedCanvasData) {
            try {
                const parsed = JSON.parse(storedCanvasData);
                // Ensure backward compatibility if they used a string 'font' instead of 'fontSize'
                const normalized = parsed.map(t => ({
                    ...t,
                    fontSize: t.fontSize || parseInt(t.font, 10) || DEFAULT_FONT_SIZE
                }));
                setTextsOnCanvas(normalized);
            } catch (e) {
                console.error("Failed to parse canvas localStorage", e);
            }
        }
    }, []);

    // Save canvas data whenever textsOnCanvas changes
    useEffect(() => {
        if (textsOnCanvas.length > 0 || localStorage.getItem('panoramicCanvasTexts')) {
            localStorage.setItem('panoramicCanvasTexts', JSON.stringify(textsOnCanvas));
        }
    }, [textsOnCanvas]);

    // Save sidebar data whenever it changes
    const updateSidebarTexts = (newTexts) => {
        setSidebarTexts(newTexts);
        localStorage.setItem('panoramicSidebarTexts', JSON.stringify(newTexts));
    };

    // Draw Canvas
    const drawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');

        // Clear entire canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw Base Image
        if (imageObj) {
            ctx.drawImage(imageObj, 0, 0);
        }

        // Draw Texts
        textsOnCanvas.forEach(t => {
            ctx.font = `${t.fontSize}px ${DEFAULT_FONT_FAMILY}`;
            ctx.fillStyle = t.color;
            ctx.textBaseline = 'top';
            ctx.fillText(t.text, t.x, t.y);
        });

        // Draw Selection Box
        const selectedObj = textsOnCanvas.find(t => t.id === selectedTextId);
        if (selectedObj) {
            ctx.font = `${selectedObj.fontSize}px ${DEFAULT_FONT_FAMILY}`;
            ctx.textBaseline = 'top';
            const textWidth = ctx.measureText(selectedObj.text).width;
            ctx.strokeStyle = '#2196f3'; // MUI Blue
            ctx.lineWidth = 2;
            ctx.strokeRect(
                selectedObj.x - 4,
                selectedObj.y - 4,
                textWidth + 8,
                selectedObj.fontSize + 8
            );
        }
    }, [imageObj, textsOnCanvas, selectedTextId]);

    // Redraw loop on state changes
    useEffect(() => {
        drawCanvas();
    }, [drawCanvas]);


    // Handlers
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setOriginalFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = canvasRef.current;
                canvas.width = img.width;
                canvas.height = img.height;
                setImageObj(img);
                setZoom(100);
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    const handleAddSidebarText = () => {
        const trimmed = newText.trim();
        if (trimmed && !sidebarTexts.includes(trimmed)) {
            updateSidebarTexts([...sidebarTexts, trimmed]);
            setNewText('');
        }
    };

    const handleDeleteSidebarText = (textToRemove) => {
        updateSidebarTexts(sidebarTexts.filter(t => t !== textToRemove));
    };

    // Drag and Drop (from sidebar to canvas OR reordering)
    const handleDragStart = (e, index, text) => {
        dragItem.current = index;
        e.dataTransfer.setData('text/plain', text);
    };

    const handleListDragEnter = (e, index) => {
        dragOverItem.current = index;
    };

    const handleListDragEnd = () => {
        if (dragItem.current !== undefined && dragOverItem.current !== undefined && dragItem.current !== dragOverItem.current) {
            const newList = [...sidebarTexts];
            const draggedText = newList[dragItem.current];
            newList.splice(dragItem.current, 1);
            newList.splice(dragOverItem.current, 0, draggedText);
            updateSidebarTexts(newList);
        }
        dragItem.current = undefined;
        dragOverItem.current = undefined;
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedText = e.dataTransfer.getData('text/plain');
        if (!droppedText) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // Handle scaling between responsive CSS displayed size and actual internal canvas coordinate system
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        const newTextObj = {
            id: Date.now(),
            text: droppedText,
            x,
            y,
            fontSize: parseInt(fontSize, 10) || DEFAULT_FONT_SIZE,
            color: color
        };

        setTextsOnCanvas(prev => [...prev, newTextObj]);
        setSelectedTextId(newTextObj.id);
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // necessary to allow dropping
    };

    // Canvas Mouse Interaction for Select and Move
    const getMousePos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    const handleCanvasMouseDown = (e) => {
        const { x, y } = getMousePos(e);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        let clickedText = null;
        // Search backwards to select the element painted on top
        for (let i = textsOnCanvas.length - 1; i >= 0; i--) {
            const t = textsOnCanvas[i];
            ctx.font = `${t.fontSize}px ${DEFAULT_FONT_FAMILY}`;
            const textWidth = ctx.measureText(t.text).width;

            if (x >= t.x && x <= t.x + textWidth && y >= t.y && y <= t.y + t.fontSize) {
                clickedText = t;
                break;
            }
        }

        if (clickedText) {
            setSelectedTextId(clickedText.id);
            setColor(clickedText.color);
            setFontSize(clickedText.fontSize);
            setIsDragging(true);
            setDragOffset({ x: x - clickedText.x, y: y - clickedText.y });
        } else {
            setSelectedTextId(null);
        }
    };

    const handleCanvasMouseMove = (e) => {
        if (!isDragging || !selectedTextId) return;
        const { x, y } = getMousePos(e);

        setTextsOnCanvas(prev => prev.map(t => {
            if (t.id === selectedTextId) {
                return { ...t, x: x - dragOffset.x, y: y - dragOffset.y };
            }
            return t;
        }));
    };

    const handleCanvasMouseUp = () => {
        setIsDragging(false);
    };

    const handleCanvasDoubleClick = (e) => {
        const { x, y } = getMousePos(e);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        let clickedTextId = null;
        for (let i = textsOnCanvas.length - 1; i >= 0; i--) {
            const t = textsOnCanvas[i];
            ctx.font = `${t.fontSize}px ${DEFAULT_FONT_FAMILY}`;
            const textWidth = ctx.measureText(t.text).width;

            if (x >= t.x && x <= t.x + textWidth && y >= t.y && y <= t.y + t.fontSize) {
                clickedTextId = t.id;
                break;
            }
        }

        if (clickedTextId) {
            setTextsOnCanvas(prev => prev.filter(t => t.id !== clickedTextId));
            if (selectedTextId === clickedTextId) {
                setSelectedTextId(null);
            }
        }
    };

    // Toolbar Actions
    const handleDeleteSelected = () => {
        if (selectedTextId) {
            setTextsOnCanvas(prev => prev.filter(t => t.id !== selectedTextId));
            setSelectedTextId(null);
        }
    };

    const handleClearCanvas = () => {
        setTextsOnCanvas([]);
        setSelectedTextId(null);
    };

    const handleSaveImage = () => {
        setSelectedTextId(null); // Deselect so border doesn't print

        // Need to wait for react state to flush the select border removal before converting to DATA URL
        setTimeout(() => {
            const canvas = canvasRef.current;
            const link = document.createElement('a');
            const fileName = originalFileName
                ? originalFileName.split('.').slice(0, -1).join('.') + '-marked.png'
                : 'panoramic-marked.png';
            link.download = fileName;
            link.href = canvas.toDataURL('image/png');
            link.click();
        }, 100);
    };

    const handleColorChange = (e) => {
        const newColor = e.target.value;
        setColor(newColor);
        localStorage.setItem('panoramicMarkerColor', newColor);
        if (selectedTextId) {
            setTextsOnCanvas(prev => prev.map(t => t.id === selectedTextId ? { ...t, color: newColor } : t));
        }
    };

    const handleFontSizeChange = (e) => {
        const newSize = parseInt(e.target.value, 10) || DEFAULT_FONT_SIZE;
        setFontSize(newSize);
        localStorage.setItem('panoramicMarkerFontSize', newSize.toString());
        if (selectedTextId) {
            setTextsOnCanvas(prev => prev.map(t => t.id === selectedTextId ? { ...t, fontSize: newSize } : t));
        }
    };

    const handleZoomChange = (newZoom) => {
        setZoom(newZoom);
    };

    return (
        <React.Fragment>
            <CssBaseline />
            <Box sx={{ display: 'flex', height: '100vh', boxSizing: 'border-box', overflow: 'hidden', width: '100%', gap: 2, p: 2, bgcolor: '#f5f5f5' }}>

                {/* Sidebar (Draggable Texts) */}
                <Paper elevation={3} sx={{ width: 280, display: 'flex', flexDirection: 'column', p: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                        Draggable Texts
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Add new text"
                            value={newText}
                            onChange={(e) => setNewText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddSidebarText()}
                        />
                        <IconButton color="primary" onClick={handleAddSidebarText}>
                            <AddCircleIcon />
                        </IconButton>
                    </Box>

                    <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={() => updateSidebarTexts([])}
                        sx={{ mb: 2 }}
                    >
                        Clear Text List
                    </Button>

                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, pr: 1 }}>
                        {sidebarTexts.map((text, index) => (
                            <Box
                                key={index}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index, text)}
                                onDragEnter={(e) => handleListDragEnter(e, index)}
                                onDragEnd={handleListDragEnd}
                                onDragOver={(e) => e.preventDefault()}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    bgcolor: '#e3f2fd',
                                    border: '1px solid #bbdefb',
                                    borderRadius: 1,
                                    cursor: 'grab',
                                    '&:active': { cursor: 'grabbing', opacity: 0.7 },
                                    '&:hover': { bgcolor: '#bbdefb' }
                                }}
                            >
                                <Typography variant="body2" sx={{ userSelect: 'none' }}>{text}</Typography>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => { e.stopPropagation(); handleDeleteSidebarText(text); }}
                                    sx={{ p: 0.5 }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        {sidebarTexts.length === 0 && (
                            <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
                                No text snippets available.<br />Add one above!
                            </Typography>
                        )}
                    </Box>
                </Paper>

                {/* Main Canvas Area */}
                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Toolbar */}
                    <Paper elevation={3} sx={{ p: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            component="label"
                            size="small"
                            startIcon={<UploadFileIcon />}
                        >
                            Upload Image
                            <input type="file" hidden accept="image/*" ref={fileInputRef} onChange={handleImageUpload} />
                        </Button>

                        <Divider orientation="vertical" flexItem />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Color:</Typography>
                            <input
                                type="color"
                                value={color}
                                onChange={handleColorChange}
                                style={{ border: 'none', height: 26, width: 32, cursor: 'pointer', backgroundColor: 'transparent' }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>Size:</Typography>
                            <TextField
                                type="number"
                                size="small"
                                value={fontSize}
                                onChange={handleFontSizeChange}
                                inputProps={{ min: 10, max: 150, style: { padding: '4px 8px', fontSize: '0.85rem' } }}
                                sx={{ width: 65 }}
                            />
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            disabled={!selectedTextId}
                            onClick={handleDeleteSelected}
                            startIcon={<DeleteIcon />}
                        >
                            Delete
                        </Button>

                        <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={handleClearCanvas}
                        >
                            Clear Canvas
                        </Button>

                        <Divider orientation="vertical" flexItem />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton onClick={() => handleZoomChange(Math.max(10, zoom - 10))} size="small" sx={{ padding: '4px' }}>
                                <ZoomOutIcon fontSize="small" />
                            </IconButton>
                            <Slider
                                size="small"
                                value={zoom}
                                min={10}
                                max={500}
                                onChange={(e, val) => handleZoomChange(val)}
                                sx={{ width: 80 }}
                            />
                            <IconButton onClick={() => handleZoomChange(Math.min(500, zoom + 10))} size="small" sx={{ padding: '4px' }}>
                                <ZoomInIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 35, fontSize: '0.8rem' }}>{zoom}%</Typography>
                        </Box>

                        <Box sx={{ flexGrow: 1 }} />

                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={handleSaveImage}
                            startIcon={<SaveIcon />}
                            disabled={!imageObj && textsOnCanvas.length === 0}
                        >
                            Save Image
                        </Button>
                    </Paper>

                    {/* Canvas Wrapper */}
                    <Paper
                        elevation={3}
                        sx={{
                            flexGrow: 1,
                            overflow: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start',
                            bgcolor: 'background.paper',
                            p: 2
                        }}
                    >
                        {!imageObj && (
                            <Box sx={{
                                width: '100%',
                                height: '100%',
                                border: '2px dashed #ccc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 2
                            }}>
                                <Typography color="text.secondary">
                                    Please upload a panoramic image to begin.
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: imageObj ? 'flex' : 'none', flexDirection: 'column', alignItems: 'center' }}>
                            <canvas
                                ref={canvasRef}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onMouseDown={handleCanvasMouseDown}
                                onMouseMove={handleCanvasMouseMove}
                                onMouseUp={handleCanvasMouseUp}
                                onMouseLeave={handleCanvasMouseUp}
                                onDoubleClick={handleCanvasDoubleClick}
                                style={{
                                    width: imageObj ? `${(imageObj.width * zoom) / 100}px` : 'auto',
                                    height: imageObj ? `${(imageObj.height * zoom) / 100}px` : 'auto',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                    cursor: isDragging ? 'grabbing' : (selectedTextId ? 'grab' : 'crosshair')
                                }}
                            />
                        </Box>
                    </Paper>
                </Box>
            </Box>
        </React.Fragment>
    );
};

export default PanoramicMarker;
